import React, { useState, useEffect } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useDocument } from "react-firebase-hooks/firestore";
import { auth, db, storage } from "../firebase";
import {
  doc,
  updateDoc,
  query,
  where,
  getDocs,
  collection,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { updateProfile } from "firebase/auth";
import { AiOutlineUser, AiOutlineMail, AiOutlineLoading } from "react-icons/ai";
import { MdWork, MdOutlineDescription } from "react-icons/md";
import { Toast } from "./Toast";

const jobOptions = [
  "Software Engineer",
  "Frontend Developer",
  "Backend Developer",
  "Full Stack Developer",
  "Product Manager",
  "UI/UX Designer",
  "Data Scientist",
  "Business Analyst",
  "Project Manager",
  "Quality Assurance Engineer",
  "DevOps Engineer",
  "Technical Support Specialist",
];

const UserProfile = () => {
  const [user] = useAuthState(auth);
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [bio, setBio] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showToast, setShowToast] = useState(false);

  const userRef = doc(db, "users", user.uid);
  const [snapshot] = useDocument(userRef);

  useEffect(() => {
    if (snapshot && snapshot.exists()) {
      const userData = snapshot.data();
      setDisplayName(user.displayName || userData.displayName || "");
      setUsername(userData.username || "");
      setEmail(userData.email || "");
      setJobTitle(userData.jobTitle || "");
      setBio(userData.bio || "");
      setProfileImage(userData.profileImage || "");
      setLoading(false);
    }
  }, [snapshot]);

  const checkUsernameExists = async (newUsername) => {
    const usersRef = query(
      collection(db, "users"),
      where("username", "==", newUsername)
    );
    const snapshot = await getDocs(usersRef);
    return !snapshot.empty;
  };

  const handleUpdateProfile = async () => {
    if (
      displayName.trim() === "" ||
      username.trim() === "" ||
      jobTitle.trim() === ""
    ) {
      setErrorMessage("Please fill in all required fields.");
      return;
    }

    const usernameExists = await checkUsernameExists(username);
    if (usernameExists && username !== (snapshot?.data()?.username || "")) {
      setErrorMessage(
        "Username already exists. Please choose a different one."
      );
      return;
    }

    try {
      await updateProfile(auth.currentUser, {
        displayName,
        photoURL: profileImage,
      });

      await updateDoc(userRef, {
        displayName,
        username,
        jobTitle,
        bio,
        profileImage,
      });

      setErrorMessage("");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (error) {
      console.error("Error updating profile:", error);
      setErrorMessage("Failed to update profile. Please try again.");
    }
  };

  const handleProfileImageChange = async (event) => {
    try {
      const file = event.target.files[0];
      if (!file) return;

      setUploading(true);
      const storageRef = ref(
        storage,
        `profile_images/${user.uid}/${file.name}`
      );
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        () => {},
        (error) => {
          console.error("Error uploading profile image:", error);
          setUploading(false);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            setProfileImage(downloadURL);
            updateDoc(userRef, { profileImage: downloadURL });
            setUploading(false);
          });
        }
      );
    } catch (error) {
      console.error("Error uploading profile image:", error);
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100">
        <AiOutlineLoading className="animate-spin text-4xl text-blue-600" />
      </div>
    );
  }

  return (
    <div className="h-[94vh] bg-gray-100 py-10 overflow-y-auto scrollbar-none">
      <div className="max-w-3xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 bg-[#9999ff] text-white">
          <h3 className="text-2xl font-semibold">Profile Information</h3>
        </div>
        <div className="px-6 py-8">
          <div className="flex flex-col md:flex-row md:items-center mb-6">
            <div className="flex-shrink-0">
              <img
                src={profileImage}
                alt="Profile"
                className="h-32 w-32 rounded-full border-4 border-white shadow-md"
              />
              {uploading && (
                <AiOutlineLoading className="animate-spin text-4xl text-blue-600 absolute" />
              )}
            </div>
            <div className="mt-4 md:mt-0 md:ml-6 flex flex-col">
              <label
                htmlFor="profileImageUpload"
                className="inline-block px-4 py-2 bg-[#9999ff] text-white font-semibold rounded-md cursor-pointer hover:bg-[#ff8989] focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {uploading ? (
                  <AiOutlineLoading className="animate-spin inline-block mr-2" />
                ) : (
                  "Change Profile Picture"
                )}
              </label>
              <input
                id="profileImageUpload"
                type="file"
                accept="image/*"
                onChange={handleProfileImageChange}
                className="hidden"
              />
            </div>
          </div>

          <div className="flex flex-col mb-6">
            <label className="text-sm font-medium text-gray-600 flex items-center">
              <AiOutlineUser className="mr-2" />
              Full Name
            </label>
            <input
              type="text"
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring focus:ring-blue-500"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>

          <div className="flex flex-col mb-6">
            <label className="text-sm font-medium text-gray-600 flex items-center">
              <AiOutlineUser className="mr-2" />
              Username
            </label>
            <input
              type="text"
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring focus:ring-blue-500"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>

          <div className="flex flex-col mb-6">
            <label className="text-sm font-medium text-gray-600 flex items-center">
              <AiOutlineMail className="mr-2" />
              Email Address
            </label>
            <input
              type="email"
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring focus:ring-blue-500"
              value={email}
              disabled
            />
          </div>

          <div className="flex flex-col mb-6">
            <label className="text-sm font-medium text-gray-600 flex items-center">
              <MdWork className="mr-2" />
              Job Title
            </label>
            <select
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring focus:ring-blue-500"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
            >
              <option value="" disabled>
                Select Job Title
              </option>
              {jobOptions.map((jobOption, index) => (
                <option key={index} value={jobOption}>
                  {jobOption}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col mb-6">
            <label className="text-sm font-medium text-gray-600 flex items-center">
              <MdOutlineDescription className="mr-2" />
              Bio
            </label>
            <textarea
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring focus:ring-blue-500"
              rows="3"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
            />
          </div>

          {errorMessage && (
            <div className="text-red-600 mb-4">{errorMessage}</div>
          )}

          <button
            type="button"
            onClick={handleUpdateProfile}
            className="w-full px-4 py-2 bg-[#7f7fdf] text-white font-semibold rounded-md hover:bg-[#ff8989] focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Update Profile
          </button>
        </div>
      </div>
      {showToast && <Toast message="Profile updated successfully!" />}
    </div>
  );
};

export default UserProfile;
